import { useEffect, useRef } from 'react'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import type { ProjectPhoto } from '@/data/content'

interface LightboxProps {
  photos: ProjectPhoto[]
  index: number
  title: string
  onIndex: (next: number) => void
  onClose: () => void
}

export default function Lightbox({ photos, index, title, onIndex, onClose }: LightboxProps) {
  const closeRef = useRef<HTMLButtonElement>(null)
  const total = photos.length
  const photo = photos[index]

  const go = (step: number) => onIndex((index + step + total) % total)

  useEffect(() => {
    closeRef.current?.focus()
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
      if (event.key === 'ArrowRight') onIndex((index + 1) % total)
      if (event.key === 'ArrowLeft') onIndex((index - 1 + total) % total)
    }
    document.addEventListener('keydown', onKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [index, total, onClose, onIndex])

  if (!photo) return null

  return (
    <div
      className="fixed inset-0 z-[70] flex flex-col bg-forest-950/95 p-4 backdrop-blur-sm sm:p-8"
      role="dialog"
      aria-modal="true"
      aria-label={`${title} photos`}
      onClick={event => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div className="flex items-center justify-between gap-4 text-bone-100">
        <p className="text-sm font-medium tabular-nums text-bone-50" aria-live="polite">
          {index + 1}/{total}
        </p>
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          aria-label="Close image viewer"
          className="flex h-10 w-10 items-center justify-center rounded-full border border-bone-50/30 text-bone-50 transition hover:bg-bone-50/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bone-50"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="relative flex min-h-0 flex-1 items-center justify-center py-4">
        <img
          key={photo.src}
          src={photo.src}
          alt={photo.alt}
          className="max-h-full max-w-full rounded-xl object-contain"
        />

        {total > 1 && (
          <>
            <button
              type="button"
              onClick={() => go(-1)}
              aria-label="Previous photo"
              className="absolute left-0 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-bone-50/30 bg-forest-950/50 text-bone-50 transition hover:bg-forest-950/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bone-50 sm:left-2"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              aria-label="Next photo"
              className="absolute right-0 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-bone-50/30 bg-forest-950/50 text-bone-50 transition hover:bg-forest-950/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bone-50 sm:right-2"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </>
        )}
      </div>
    </div>
  )
}
